// src/pages/admin/AdminResourcesPage.tsx
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAdminResources, useDeleteResource, usePublishResource, useUnpublishResource } from '../hooks';
import { useAdminStore } from '@/features/admin/stores/adminStore';
import { useToast } from '@/core/hooks/useToast';
import { cn } from '@/core/lib/utils';

export const AdminResourcesPage: React.FC = () => {
  const [page, setPage] = useState(1);
  const { resourceFilters, setResourceFilters } = useAdminStore();
  const { toast } = useToast();
  
  const { data, isLoading } = useAdminResources({
    page,
    limit: 20,
    search: resourceFilters.search,
    category_id: resourceFilters.categoryId,
    status: resourceFilters.status,
    type: resourceFilters.type,
  });

  const deleteMutation = useDeleteResource();
  const publishMutation = usePublishResource();
  const unpublishMutation = useUnpublishResource();

  const handleDelete = (id: string) => {
    if (confirm('Are you sure you want to delete this resource?')) {
      deleteMutation.mutate(id, {
        onSuccess: () => toast({ title: 'Resource deleted', variant: 'success' }),
      });
    }
  };

  const handlePublish = (id: string) => {
    publishMutation.mutate(id, {
      onSuccess: () => toast({ title: 'Resource published', variant: 'success' }),
    });
  };

  const handleUnpublish = (id: string) => {
    unpublishMutation.mutate(id, {
      onSuccess: () => toast({ title: 'Resource unpublished', variant: 'success' }),
    });
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-display font-semibold text-navy">Resources</h1>
          <p className="text-warm-gray">Manage educational content and guides</p>
        </div>
        <Link
          to="/admin/resources/new"
          className="px-4 py-2 bg-sage text-white rounded-lg hover:bg-sage-dark transition-colors"
        >
          Add Resource
        </Link>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-gray-200 p-4">
        <div className="flex flex-wrap gap-4">
          <input
            type="text"
            placeholder="Search resources..."
            value={resourceFilters.search || ''}
            onChange={(e) => setResourceFilters({ search: e.target.value })}
            className="flex-1 min-w-[200px] px-4 py-2 border border-gray-200 rounded-lg"
          />
          <select
            value={resourceFilters.status || ''}
            onChange={(e) => setResourceFilters({ status: e.target.value || undefined })}
            className="px-4 py-2 border border-gray-200 rounded-lg"
          >
            <option value="">All Statuses</option>
            <option value="draft">Draft</option>
            <option value="published">Published</option>
            <option value="archived">Archived</option>
          </select>
          <select
            value={resourceFilters.type || ''}
            onChange={(e) => setResourceFilters({ type: e.target.value || undefined })}
            className="px-4 py-2 border border-gray-200 rounded-lg"
          >
            <option value="">All Types</option>
            <option value="article">Article</option>
            <option value="guide">Guide</option>
            <option value="video">Video</option>
            <option value="checklist">Checklist</option>
          </select>
        </div>
      </div>

      {/* Resources List */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        {isLoading ? (
          <div className="p-6 space-y-4">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-16 bg-gray-100 animate-pulse rounded" />
            ))}
          </div>
        ) : (
          <table className="w-full">
            <thead className="bg-cream">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-warm-gray uppercase">Title</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-warm-gray uppercase">Type</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-warm-gray uppercase">Status</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-warm-gray uppercase">Views</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-warm-gray uppercase">Updated</th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-warm-gray uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {data?.items.map((resource) => (
                <tr key={resource.id} className="hover:bg-cream/50">
                  <td className="px-4 py-3">
                    <p className="font-medium text-navy">{resource.title}</p>
                    <p className="text-sm text-warm-gray truncate max-w-xs">{resource.excerpt}</p>
                  </td>
                  <td className="px-4 py-3">
                    <span className="px-2 py-1 bg-gray-100 text-gray-700 rounded text-xs capitalize">
                      {resource.resource_type}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span className={cn(
                      'px-2 py-1 rounded text-xs font-medium capitalize',
                      resource.status === 'published' ? 'bg-green-100 text-green-800' :
                      resource.status === 'draft' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-gray-100 text-gray-800'
                    )}>
                      {resource.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-navy">{resource.view_count}</td>
                  <td className="px-4 py-3 text-sm text-warm-gray">
                    {new Date(resource.updated_at).toLocaleDateString()}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex justify-end gap-2">
                      <Link
                        to={`/admin/resources/edit/${resource.id}`}
                        className="text-sage hover:text-sage-dark"
                      >
                        Edit
                      </Link>
                      {resource.status === 'draft' ? (
                        <button onClick={() => handlePublish(resource.id)} className="text-blue-600 hover:text-blue-800">
                          Publish
                        </button>
                      ) : resource.status === 'published' ? (
                        <button onClick={() => handleUnpublish(resource.id)} className="text-yellow-600 hover:text-yellow-800">
                          Unpublish
                        </button>
                      ) : null}
                      <button onClick={() => handleDelete(resource.id)} className="text-red-600 hover:text-red-800">
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default AdminResourcesPage;



